import React, { useState } from 'react';
import { OnboardingStatusResponse } from '../types';

interface OnboardingPaymentProps {
  token: string;
  onboardingData: OnboardingStatusResponse;
  onStepComplete: (step: string, data?: Record<string, unknown>) => Promise<void>;
  error: string | null;
}

/**
 * Payment setup step for Fair Manager onboarding
 * Issue #1455 - Step 5 of 7: Stripe Connect setup (optional, can be done later)
 */
const OnboardingPayment: React.FC<OnboardingPaymentProps> = ({
  onStepComplete,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSetupLater = async () => {
    try {
      setLoading(true);
      setError(null);

      await onStepComplete('payment', { setup_later: true });

      // Move to next step
      await onStepComplete('fair_review');
    } catch (err) {
      console.error("Failed to save payment:", err);
      setError('Failed to proceed. Please try again.');
      console.error('Failed to skip payment setup:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSetupNow = async () => {
    // In a full implementation, this would redirect to Stripe Connect OAuth flow
    setError('Stripe Connect integration will be available in a future update. Please skip for now.');
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment & Payout Setup</h2>
        <p className="text-gray-600">
          Set up your payment account to receive payouts from booth reservations. This step is
          optional and can be completed later from your dashboard.
        </p>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
        <div className="flex items-start">
          <svg
            className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5"
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <div>
            <h4 className="text-sm font-semibold text-yellow-900">
              Payment Setup Required for Paid Fairs
            </h4>
            <p className="text-sm text-yellow-800 mt-1">
              If you plan to charge booth fees, you'll need to complete payment setup before
              vendors can make reservations. You can always set this up later from your dashboard.
            </p>
          </div>
        </div>
      </div>

      {/* Payment method info */}
      <div className="border border-gray-200 rounded-lg p-6 mb-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg
              className="h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
              />
            </svg>
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Secure Payment Processing</h3>
            <p className="text-gray-600 mb-4">
              We use <strong>Stripe Connect</strong> for secure payment processing. Stripe is
              trusted by millions of businesses worldwide.
            </p>
            <ul className="space-y-2 text-gray-600">
              <li className="flex items-start">
                <svg
                  className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Bank-level security and encryption</span>
              </li>
              <li className="flex items-start">
                <svg
                  className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Direct deposits to your bank account</span>
              </li>
              <li className="flex items-start">
                <svg
                  className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Transparent fee structure with no hidden costs</span>
              </li>
              <li className="flex items-start">
                <svg
                  className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Automatic payout scheduling</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4" role="alert">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-stretch sm:items-center">
        <button
          type="button"
          onClick={handleSetupLater}
          disabled={loading}
          className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50"
        >
          Set Up Later
        </button>
        <button
          type="button"
          onClick={handleSetupNow}
          disabled={loading}
          className={`
            px-6 py-2 rounded-md font-medium transition-colors
            ${
              loading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-teal-600 text-white hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500'
            }
          `}
        >
          {loading ? 'Loading...' : 'Set Up Stripe Connect'}
        </button>
      </div>

      <p className="mt-4 text-sm text-gray-500 text-center">
        You can complete payment setup anytime from your Fair Manager Dashboard
      </p>
    </div>
  );
};

export default OnboardingPayment;
